// POS Deals (owner, 26 Oct 2026; plan agent-status/POS-DEALS-PLAN.md r2).
// A deal sells as its component products, ordinary cart lines tagged with
// dealId. These helpers mirror the backend (clix-shared/utils/dealSplit.js).
import type { Deal } from 'store/api/deal/types';
import type { CartItem } from 'store/api/cart/types';

const toCents = (value?: number | string | null) =>
  Math.round(Number(value || 0) * 100);

interface DealComponentPrices {
  count: number;
  minPrice?: number | string | null;
  retailPrice?: number | string | null;
}

/** The deal price limits: Σ minimums (capped at Σ retail) and Σ retail. */
export const dealPriceLimits = (components: DealComponentPrices[] = []) => {
  const retail = components.reduce(
    (sum, { count, retailPrice }) => sum + toCents(retailPrice) * Number(count),
    0
  );
  const minimum = components.reduce(
    (sum, { count, minPrice }) => sum + toCents(minPrice) * Number(count),
    0
  );
  return { maxPrice: retail / 100, minPrice: Math.min(minimum, retail) / 100 };
};

/** The deal's components with their products' current prices. */
export const dealComponents = (deal?: Deal) =>
  (deal?.products ?? []).map(({ count, product }) => ({
    count: Number(count),
    minPrice: product?.minPrice,
    retailPrice: product?.retailPrice,
  }));

/**
 * Units of a product already in the cart that come from the booth: every
 * non-exchange, non-shipping line (deals make several lines of one product
 * normal), as the backend's stock check counts them.
 */
export const boothUnitsInCart = (items: CartItem[] = [], productId?: number) =>
  items
    .filter(
      (item) =>
        !item.isExchange && !item.isShipping && item.productId === productId
    )
    .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

/**
 * How many more of this deal the booth can give: for each component,
 * floor((booth count − units in the cart) ÷ count), the lowest of them. A
 * component with no stock row at the show gives 0 (the backend refuses it).
 */
export const bundlesLeft = (
  deal: Deal,
  boothCounts: Map<number, number>,
  items: CartItem[] = []
) => {
  if (!deal.products?.length) {
    return 0;
  }
  return Math.max(
    0,
    Math.min(
      ...deal.products.map(({ count, productId }) => {
        const booth = boothCounts.get(productId);
        if (
          booth === undefined ||
          !Number.isInteger(Number(count)) ||
          count < 1
        ) {
          return 0;
        }
        return Math.floor(
          (booth - boothUnitsInCart(items, productId)) / Number(count)
        );
      })
    )
  );
};

/** True when a component's product is gone (deleted, or removed from the deal). */
export const hasDeletedComponent = (deal: Deal) =>
  (deal.products ?? []).some(({ product }) => !product || !!product.deletedAt);

/** 'Bundle includes' text: "1 x Neck". */
export const dealComponentLabels = (deal?: Deal) =>
  (deal?.products ?? []).map(
    ({ count, product }) => `${Number(count)} x ${product?.name ?? 'Product'}`
  );

/** A deal's SKU, or DEAL-<id> when it has none (as in the SaaS). */
export const dealSku = (deal?: Pick<Deal, 'id' | 'sku'>) =>
  deal?.sku || `DEAL-${deal?.id}`;

/** The office's view of a deal: can it sell today? */
export const dealStatus = (deal: Deal, today: string) => {
  if (hasDeletedComponent(deal)) {
    return 'Component deleted';
  }
  if (deal.validUntil && String(deal.validUntil).slice(0, 10) < today) {
    return 'Expired';
  }
  if (Number(deal.salePrice) < dealPriceLimits(dealComponents(deal)).minPrice) {
    return 'Below minimums';
  }
  return 'Active';
};

/**
 * Cart lines in order, with each deal's lines gathered where its first line
 * stands: `{ dealId, items }` for a deal, `{ item }` for any other line.
 */
export const groupCartLines = (items: CartItem[] = []) =>
  items.reduce<
    Array<{ item: CartItem } | { dealId: number; items: CartItem[] }>
  >((groups, item) => {
    if (!item.dealId) {
      groups.push({ item });
      return groups;
    }
    const group = groups.find(
      (entry) => 'dealId' in entry && entry.dealId === item.dealId
    ) as { dealId: number; items: CartItem[] } | undefined;
    if (group) {
      group.items.push(item);
    } else {
      groups.push({ items: [item], dealId: item.dealId });
    }
    return groups;
  }, []);
